/**
 * useDeliveryPoint: where the shopper wants things delivered, as a point the
 * location helpers in utils/vendorOutlets can measure from.
 * ─────────────────────────────────────────────────────────────────────────────
 * The browse location the shopper set (redux `userLocation`), checked against
 * the active market the same way the coverage hooks check it, so a stale
 * point from another country never ranks one branch above another. Null when
 * no usable location is set; every caller then falls back to open state and
 * the vendor's own row.
 *
 * Shared by the Brands and Restaurants listings (which of a chain's locations
 * a card opens on) and by both storefronts' location pickers (how far each
 * branch is), 4 Oct 2026. Memoised on its inputs, so a list built from it is
 * not rebuilt on every render.
 *
 * The point also carries the place it names (utils/deliveryPoint), so the
 * storefront picker can say what its distances are measured from.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { normalizeMarketLatLng } from "../config/market";
import { deliveryPointOf } from "../utils/deliveryPoint";

export default function useDeliveryPoint() {
  const latitude = useSelector((state) => state.userLocation?.latitude);
  const longitude = useSelector((state) => state.userLocation?.longitude);
  const address = useSelector((state) => state.userLocation?.address);
  const source = useSelector((state) => state.userLocation?.locationSource);
  return useMemo(() => {
    const coordinates = normalizeMarketLatLng(latitude, longitude);
    return coordinates
      ? deliveryPointOf({
          latitude: coordinates.lat,
          longitude: coordinates.lng,
          address,
          source,
        })
      : null;
  }, [latitude, longitude, address, source]);
}
